import { Component } from '@theme/component';
import { isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';
import { DialogOpenEvent } from '@theme/dialog';

const chartDataCache = new Map();
const chartUnits = ['cm', 'inches'];

export function getChartForProduct(data, handle) {
  if (data?.version !== 1 || !data.products || !data.charts) return null;
  if (!Object.hasOwn(data.products, handle)) return null;

  const chartId = data.products[handle];
  if (typeof chartId !== 'string' || !Object.hasOwn(data.charts, chartId)) return null;

  const chart = data.charts[chartId];
  if (!chart || !Array.isArray(chart.tables) || chart.tables.length === 0) return null;
  if (!chart.tables.every(isValidTable)) return null;

  const units = chartUnits.filter((unit) => chart.tables.every((table) => hasValidRows(table, unit)));
  if (units.length === 0) return null;

  return { ...chart, units };
}

function hasValidRows(table, unit) {
  const rows = table.rows?.[unit];
  return (
    Array.isArray(rows) &&
    rows.length > 0 &&
    rows.every(
      (row) =>
        Array.isArray(row) &&
        row.length === table.columns.length &&
        row.every((cell) => typeof cell === 'string')
    )
  );
}

function isValidTable(table) {
  const valid = (
    typeof table?.title === 'string' &&
    Array.isArray(table.columns) &&
    table.columns.length > 1 &&
    table.columns.every((column) => typeof column === 'string') &&
    chartUnits.some((unit) => hasValidRows(table, unit))
  );
  if (!valid) return false;
  if (hasValidRows(table, 'cm') && hasValidRows(table, 'inches')) {
    const cmRows = table.rows.cm;
    const inchRows = table.rows.inches;
    return cmRows.length === inchRows.length && cmRows.every((row, index) => row[0] === inchRows[index][0]);
  }
  return true;
}

function appendTextLines(parent, lines, className) {
  if (!Array.isArray(lines)) return;
  for (const line of lines) {
    if (typeof line !== 'string' || line.trim() === '') continue;
    const paragraph = document.createElement('p');
    paragraph.className = className;
    paragraph.textContent = line;
    parent.append(paragraph);
  }
}

export class GiftifyMamaSizeChart extends Component {
  requiredRefs = ['trigger', 'dialog', 'closeButton', 'units', 'unitStatus', 'content'];

  #controller;
  #variantController;
  #pendingVariantUpdate;
  #chart = null;
  #unit = 'cm';
  #opener = null;

  connectedCallback() {
    super.connectedCallback();
    this.#variantController?.abort();
    this.#variantController = new AbortController();
    this.closest('variant-picker')?.addEventListener(ProductSelectEvent.eventName, this.#handleProductSelect, {
      signal: this.#variantController.signal,
    });
    this.closest('quick-add-dialog')?.addEventListener(DialogOpenEvent.eventName, this.#handleQuickAddOpen, {
      signal: this.#variantController.signal,
    });
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.closeDialog(false);
    this.#controller?.abort();
    this.#variantController?.abort();
    this.#pendingVariantUpdate = null;
    super.disconnectedCallback();
  }

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = async (event) => {
    const picker = this.closest('variant-picker');
    if (!picker || event.target !== picker) return;
    const controller = this.#variantController;
    this.#pendingVariantUpdate = event;

    try {
      await event.promise;
    } catch {
      return;
    }
    if (
      controller?.signal.aborted ||
      this.#pendingVariantUpdate !== event ||
      !this.isConnected ||
      this.closest('variant-picker') !== picker
    ) {
      return;
    }

    // The picker promise settles after morph, even when equal subtrees skip callbacks.
    this.#pendingVariantUpdate = null;
    this.updatedCallback();
  };

  #handleQuickAddOpen = (event) => {
    const dialog = this.closest('quick-add-dialog');
    if (!dialog || event.target !== dialog || !this.isConnected || !this.hidden) return;
    this.updatedCallback();
  };

  async #initialize() {
    this.closeDialog(false);
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.hidden = true;
    this.#chart = null;

    const { dialog } = this.refs;
    dialog.addEventListener('cancel', this.#handleCancel, { signal });
    dialog.addEventListener('close', this.#handleClose, { signal });
    dialog.addEventListener('keydown', this.#handleKeyDown, { signal });
    dialog.addEventListener('click', this.#handleBackdrop, { signal });
    window.addEventListener('resize', () => {
      if (dialog.open) this.#updateTableScrollStates();
    }, { signal });

    const chartUrl = this.dataset.chartUrl;
    const handle = this.dataset.productHandle;
    if (!chartUrl || !handle) return;

    try {
      let data = chartDataCache.get(chartUrl);
      if (!data) {
        const response = await fetch(chartUrl, { signal, credentials: 'omit' });
        if (!response.ok) return;
        data = await response.json();
        if (signal.aborted || !this.isConnected) return;
        chartDataCache.set(chartUrl, data);
      }
      if (signal.aborted || !this.isConnected) return;

      const chart = getChartForProduct(data, handle);
      if (!chart) return;
      this.#chart = chart;
      this.#unit = chart.units.includes(this.dataset.defaultUnit) ? this.dataset.defaultUnit : chart.units[0];
      this.#renderChart();
      this.hidden = false;
    } catch (error) {
      if (error.name !== 'AbortError') this.hidden = true;
    }
  }

  openDialog() {
    const { dialog, trigger, closeButton } = this.refs;
    if (!this.#chart || this.hidden || dialog.open) return;
    this.#opener = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
    dialog.showModal();
    this.#updateTableScrollStates();
    lockScroll(dialog);
    trigger.setAttribute('aria-expanded', 'true');
    closeButton.focus({ preventScroll: true });
  }

  closeDialog(restoreFocus = true) {
    const { dialog, trigger } = this.refs;
    if (!(dialog instanceof HTMLDialogElement)) return;
    const wasOpen = dialog.open;
    if (wasOpen) dialog.close();
    unlockScroll(dialog);
    trigger?.setAttribute('aria-expanded', 'false');
    if (wasOpen && restoreFocus !== false && this.#opener?.isConnected) {
      this.#opener.focus({ preventScroll: true });
    }
    this.#opener = null;
  }

  selectUnit(event) {
    const unit = event.target instanceof HTMLElement ? event.target.dataset.unit : undefined;
    if (!this.#chart?.units.includes(unit) || unit === this.#unit) return;
    this.#unit = unit;
    this.#renderChart();
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleClose = () => {
    if (this.refs.dialog.open) return;
    unlockScroll(this.refs.dialog);
    this.refs.trigger.setAttribute('aria-expanded', 'false');
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Escape') event.stopPropagation();
  };

  #handleBackdrop = (event) => {
    const { dialog } = this.refs;
    const outside = isClickedOutside(event, dialog);
    if (event.target === dialog || outside) event.stopPropagation();
    if (outside) this.closeDialog();
  };

  #updateTableScrollState(wrapper) {
    const overflow = wrapper.scrollWidth > wrapper.clientWidth + 1;
    const remaining = wrapper.scrollWidth - wrapper.clientWidth - Math.abs(wrapper.scrollLeft);
    wrapper.dataset.overflow = String(overflow);
    wrapper.dataset.scrollEnd = String(!overflow || remaining <= 2);
  }

  #updateTableScrollStates() {
    for (const wrapper of this.refs.content.querySelectorAll('.gfm-size-chart__table-scroll')) {
      this.#updateTableScrollState(wrapper);
    }
  }

  #renderChart() {
    if (!this.#chart) return;
    const { content, units, unitStatus, unitButtons = [] } = this.refs;
    const unitLabel = this.#unit === 'cm' ? this.dataset.unitCm : this.dataset.unitInches;

    for (const button of unitButtons) {
      button.hidden = !this.#chart.units.includes(button.dataset.unit);
      button.setAttribute('aria-pressed', String(button.dataset.unit === this.#unit));
    }
    units.hidden = this.#chart.units.length < 2;
    unitStatus.textContent = unitLabel;

    const fragment = document.createDocumentFragment();
    for (const table of this.#chart.tables) {
      const section = document.createElement('section');
      section.className = 'gfm-size-chart__section';
      const heading = document.createElement('h3');
      heading.className = 'gfm-size-chart__table-heading';
      heading.textContent = table.title;
      section.append(heading);

      const wrapper = document.createElement('div');
      wrapper.className = 'gfm-size-chart__table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', `${table.title} · ${unitLabel}`);
      wrapper.style.setProperty('--gfm-chart-columns', String(table.columns.length));
      wrapper.dataset.compact = String(table.columns.length <= 4);
      if (table.columns.length > 4) {
        const hint = document.createElement('span');
        hint.className = 'gfm-size-chart__scroll-hint';
        hint.setAttribute('aria-hidden', 'true');
        hint.textContent = '↔';
        section.append(hint);
        wrapper.addEventListener('scroll', () => this.#updateTableScrollState(wrapper));
      }
      const tableElement = document.createElement('table');
      tableElement.className = 'gfm-size-chart__table';
      const caption = document.createElement('caption');
      caption.className = 'visually-hidden';
      caption.textContent = `${table.title} · ${unitLabel}`;
      tableElement.append(caption);

      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');
      for (const column of table.columns) {
        const th = document.createElement('th');
        th.scope = 'col';
        th.textContent = column;
        headerRow.append(th);
      }
      thead.append(headerRow);
      tableElement.append(thead);

      const tbody = document.createElement('tbody');
      for (const row of table.rows[this.#unit]) {
        const tr = document.createElement('tr');
        row.forEach((value, index) => {
          const cell = document.createElement(index === 0 ? 'th' : 'td');
          if (index === 0) cell.scope = 'row';
          cell.textContent = value;
          tr.append(cell);
        });
        tbody.append(tr);
      }
      tableElement.append(tbody);
      wrapper.append(tableElement);
      section.append(wrapper);
      appendTextLines(section, table.notes, 'gfm-size-chart__note');
      fragment.append(section);
    }

    appendTextLines(fragment, this.#chart.notes, 'gfm-size-chart__note');
    if (Array.isArray(this.#chart.measurementInstructions)) {
      const instructions = document.createElement('ul');
      instructions.className = 'gfm-size-chart__instructions';
      for (const line of this.#chart.measurementInstructions) {
        if (typeof line !== 'string' || line.trim() === '') continue;
        const item = document.createElement('li');
        item.textContent = line;
        instructions.append(item);
      }
      if (instructions.children.length) fragment.append(instructions);
    }
    content.replaceChildren(fragment);
    if (this.refs.dialog.open) this.#updateTableScrollStates();
  }
}

if (!customElements.get('giftifymama-size-chart')) {
  customElements.define('giftifymama-size-chart', GiftifyMamaSizeChart);
}
